import { useGetUsersInfinite } from '@pba/react-chat-core';
import type { RequiredGeneric } from '@pba/react-utils';
import { useGetUserFullNameWithFallback } from '@pba/react-utils';
import { type ChatUser, createChatUser } from 'models';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { EntitySnippet, InfinityScrollSelect } from 'ui';
import { useDebounce } from 'common/hooks';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependencies
import { useGetNodeUserAvatar } from 'features/users/hooks';

export type ExtendedChatGroupAddMembersFormValues = {
  users?: ChatUser[];
};

type ChatGroupAddMemberOption = {
  label: ReactNode;
  user: ChatUser;
  value: string;
};

const PAGE_SIZE = 10;

type P = {
  onChange?: (users: ChatUser[]) => void;
  value?: ChatUser[];
};

const ChatGroupAddMembersSelect = <T extends ExtendedChatGroupAddMembersFormValues = never>({
  onChange,
  value,
}: RequiredGeneric<T, P>) => {
  const getAvatarUrl = useGetNodeUserAvatar();
  const getUserFullNameWithFallback = useGetUserFullNameWithFallback();
  const [search, setSearch] = useState<string>();
  const { isDebouncing, value: debouncedSearch } = useDebounce(search);
  const {
    data: usersData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
  } = useGetUsersInfinite({
    filter: { search: debouncedSearch },
    options: { throwOnError: false },
    pagination: { pageSize: PAGE_SIZE },
  });
  // TODO: filter out users that are already in the group
  const options = useMemo(() => usersData?.pages.flat().map((user) => createChatUser(user)), [usersData?.pages]);

  const handleChange = (option?: ChatGroupAddMemberOption | ChatGroupAddMemberOption[]) => {
    if (!option || Array.isArray(option)) {
      return;
    }

    onChange?.([...(value ?? []), option.user]);
    setSearch('');
  };

  const getUserLabel = useCallback(
    (user: ChatUser) => getUserFullNameWithFallback(user),
    [getUserFullNameWithFallback],
  );

  const selectOptions = useMemo(
    () =>
      (options ?? []).map((user) => ({
        label: (
          <EntitySnippet
            avatarProps={{ src: getAvatarUrl({ userId: user.id }) }}
            descriptionProps={{ children: user.email }}
            titleProps={{ children: getUserLabel(user) }}
          />
        ),
        user,
        value: user.id,
      })),
    [getAvatarUrl, getUserLabel, options],
  );

  return (
    <InfinityScrollSelect<ChatGroupAddMemberOption>
      handleRequestMoreData={fetchNextPage}
      hasMoreData={hasNextPage}
      loading={isLoading || isFetching || isDebouncing}
      onChange={handleChange}
      options={selectOptions}
      placeholder="Search members"
      showSearch={{
        filterOption: false,
        onSearch: setSearch,
        searchValue: search ?? '',
      }}
      value={null}
    />
  );
};

export default ChatGroupAddMembersSelect;
